import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useI18n } from "@/i18n/I18nContext";
import { getPost, getPosts } from "@/data/blog";
import { Reveal } from "@/components/Reveal";
import { ArrowLeft, Clock } from "lucide-react";

const SITE_URL = "https://www.ilakeitbellagio.it";
const T = {
  it: { back: "Tutti gli articoli", read: "min di lettura", cta: "Prenota il tuo soggiorno a Bellagio", ctaText: "Vivi Bellagio e il Lago di Como dal nostro B&B familiare." },
  en: { back: "All articles", read: "min read", cta: "Book your stay in Bellagio", ctaText: "Experience Bellagio and Lake Como from our family-run B&B." },
  fr: { back: "Tous les articles", read: "min de lecture", cta: "Réservez votre séjour à Bellagio", ctaText: "Vivez Bellagio et le lac de Côme depuis notre B&B familial." },
};

export default function BlogPostPage() {
  const { lang: ctxLang } = useI18n();
  const { slug, lang: routeLang } = useParams();
  const lang = routeLang || ctxLang;
  const tx = T[lang] || T.it;
  const post = getPost(slug, lang);
  if (!post) return <Navigate to={`/${lang}/blog`} replace />;

  const url = `${SITE_URL}/${lang}/blog/${post.slug}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.cover,
    "datePublished": post.date,
    "author": { "@type": "Organization", "name": "B&B I Lake It Bellagio" },
    "publisher": { "@type": "Organization", "name": "B&B I Lake It Bellagio" },
    "mainEntityOfPage": url,
  };

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{`${post.title} | I Lake It Bellagio`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={url} />
        <link rel="alternate" hrefLang="it" href={`${SITE_URL}/it/blog/${post.slug}`} />
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/en/blog/${post.slug}`} />
        <link rel="alternate" hrefLang="fr" href={`${SITE_URL}/fr/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.cover} />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <article className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to={`/${lang}/blog`} data-testid="blog-back"
            className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#1D3557] transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> {tx.back}
          </Link>
          <Reveal>
            <div className="flex items-center gap-3 text-xs text-[#5C5C5C] mb-4">
              <span className="px-2.5 py-1 rounded-full bg-[#1D3557]/10 text-[#1D3557] font-medium">{post.category}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.readMin} {tx.read}</span>
            </div>
            <h1 data-testid="blog-post-title" className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight leading-tight">{post.title}</h1>
          </Reveal>
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 my-10">
          <Reveal>
            <div className="aspect-[16/9] overflow-hidden rounded-2xl">
              <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="prose-custom space-y-5">
            {post.body.map((para, i) =>
              para.startsWith("## ") ? (
                <h2 key={i} className="font-display text-2xl md:text-3xl text-[#1A1A1A] pt-4">{para.replace("## ", "")}</h2>
              ) : (
                <p key={i} className="text-base md:text-lg text-[#5C5C5C] leading-relaxed">{para}</p>
              )
            )}
          </div>

          <div className="mt-14 p-8 rounded-2xl bg-[#1D3557] text-center">
            <h3 className="font-display text-2xl text-white mb-2">{tx.cta}</h3>
            <p className="text-white/85 mb-6">{tx.ctaText}</p>
            <Link to={`/${lang}/prenota`} data-testid="blog-cta-book"
              className="inline-flex items-center px-6 py-3 rounded-full bg-white text-[#1D3557] font-medium hover:bg-white/90 transition-colors">
              {tx.cta}
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
